import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { isMobile } from 'react-device-detect';
import CalloutLine from './calloutLine';
import { Html } from './Html';
import { Html as ThreeHtml } from '@react-three/drei';
import CanvasCircle from './canvasCircle';
import Canvas from './canvas';
import { CANVAS_W, CANVAS_H } from './utils';
import HotspotContainer from './hotspotContainer';
import HotspotIcon from '../icons';
import MEDIA_TYPES from 'consts/mediaType.const';
import {
  get2DScreenPosition,
  distanceBetween2dCoordinates,
} from 'utils/positionHelper';
import { getCenterScreen } from 'containers/world/utils';
import { trackTourContentVisible } from 'gaTracking';
import { useDispatch, useSelector } from 'react-redux';
import { HOTSPOT_UI, LAYOUT_UI } from 'consts/ui.const';
import { checkIsShowHotspotMap, subscribeFilterHotspots } from 'utils';
import CanvasPulsePoint from './canvasPulsePoint';
import AvoriazHotspot from 'components/AvoriazLayout/AvoriazHotspot';
import ClearSpaceHotspot from 'containers/world/sceneController/ClearSpaceHotspot/ClearSpaceHotspot';
// import MediaHotspot from './mediaHotspotLine';
import SceneFlycamOutdoorHotspot from './sceneFlycamOutdoorHotspot';
import NTVOutdoorHotspotStyle from 'components/NTVOutdoorHotspotStyle/NTVOutdoorHotspotStyle';
import NTVMediaHotspotStyle from 'components/NTVOutdoorHotspotStyle/NTVMediaHotspotStyle';
import DPSceneHs from 'components/DPHotspot/DPSceneHs';
import { ROTATED_DIAGONAL_CONFIGS, HOTSPOT_RENDER_ORDER } from 'components/NTVOutdoorHotspotStyle/constants';
import NTVRotatedDiagonalHotspot from 'components/NTVOutdoorHotspotStyle/RotatedDiagonalHotspot/NTVRotatedDiagonalHotspot';

const MAX_VISIBLE_DISTANCE = 150;
const HOVERzINDEX = '100';
const zIndexRange1 = [50, 0];
const zIndexRangeHover = [100, 100];

const styles = {
  width: `${CANVAS_W}px`,
  height: `${CANVAS_H}px`,
  pointerEvents: 'none',
};

const LineSingle = (props) => {
  const { media, hotspot, scene, currentPano, onSelect } = props;
  const dispatch = useDispatch();

  const { camera } = useThree();
  const canvasRef = useRef();
  const hotspotRef = useRef();
  const elRef = useRef();
  const [zIndex, setZIndex] = useState('0');
  const [zIndexRange, setZIndexRange] = useState(zIndexRange1);
  const [hovering, setHovering] = useState(false);
  const [isFilteredOut, setIsFilteredOut] = useState(false);
  const hotspotIdRef = useRef(media?._id || hotspot?._id);
  const trackingRef = useRef(false);
  const params = useSelector(({ searchParams }) => searchParams);
  const hotspotStyle = useSelector(({ tour }) => tour.hotspotStyle);
  const isAquaStyle = useSelector(({ tour }) => tour.menuStyle === 'aqua');
  const isAvoriazStyle = useSelector(
    ({ tour }) => tour.menuStyle === LAYOUT_UI.AVORIAZ
  );
  const isClearSpaceStyle = useSelector(
    ({ tour }) => tour.menuStyle === LAYOUT_UI.CLEAR_SPACE
  );

  const isCalloutStyle = useSelector(
    ({ searchParams }) => searchParams['hotspot-ui'] === HOTSPOT_UI.CALLOUT
  );
  const isTag = useMemo(() => {
    return media?.type === MEDIA_TYPES.INFO_TAG;
  }, [media]);

  // Update hotspotId ref when media/hotspot changes
  useEffect(() => {
    hotspotIdRef.current = media?._id || hotspot?._id;
  }, [media?._id, hotspot?._id]);

  // Subscribe to hotspot filter events (replay last value on subscribe)
  useEffect(() => {
    const unsubscribe = subscribeFilterHotspots((visibleHotspotIds) => {
      const currentId = hotspotIdRef.current;
      if (visibleHotspotIds === null) {
        setIsFilteredOut(false);
      } else if (currentId) {
        const shouldShow = visibleHotspotIds.includes(currentId);
        setIsFilteredOut(!shouldShow);
      }
    }, true); // replay = true to get last emitted value
    return unsubscribe;
  }, []);

  useFrame(() => {
    if (isMobile) {
      const [x, y, z] = props.position;
      const xyHotSpotPos = get2DScreenPosition(
        new THREE.Vector3(x, y, z),
        camera,
        window.innerWidth,
        window.innerHeight
      );
      const screenCenter = getCenterScreen();
      const distance = distanceBetween2dCoordinates(xyHotSpotPos, screenCenter);
      if (distance <= MAX_VISIBLE_DISTANCE) {
        setHovering(true);
      } else {
        setHovering(false);
      }
    }
  });

  const onHotspotVisible = useCallback(() => {
    if (props.media && !trackingRef.current) {
      trackTourContentVisible({
        media_id: props.media._id,
        tour_id: props.tour.id,
        scene_id: props.currentPano._id,
      });

      trackingRef.current = true;
    }
  }, [props.media, props.currentPano._id, props.tour.id]);

  const isShowMapFloorPlanButton = useMemo(() => {
    return checkIsShowHotspotMap(hotspot);
  }, [hotspot]);

  const configsData = useMemo(() => {
    if (!props?.hotspot?.configs) return null;
    return JSON.parse(props?.hotspot?.configs);
  }, [props?.hotspot?.configs]);

  const MEDIA_DATA = {
    'toan-khu-tang-1-a2b8ea40': {
      'bcons-solary-360-block_ab-v3-11046756': 2,
      'toan-canh-ho-boi-bc202d98': 1.7,
    },

    'toan-canh-ho-boi-bc202d98': {
      'bcons-solary-360-block_ab-v9-41ac6015': 1.7,
    },
  };

  const checkMediaLineHeight = (currentPano, hotspot) => {
    const heights = MEDIA_DATA[currentPano.id]?.[hotspot.id];

    if (heights) {
      return heights;
    }
    return 1;
  };

  const mediaHeight = useMemo(() => {
    // if (!media) return 0;
    return checkMediaLineHeight(currentPano, hotspot, media);
    // eslint-disable-next-line
  }, [currentPano, hotspot, media]);

  const hoverToggled = (isHover) => {
    const parentNode = elRef.current && elRef.current.parentNode;
    setHovering(Boolean(isHover));
    if (isHover) {
      setZIndexRange(zIndexRangeHover);
      props.setHotspotHovering(true);
      if (parentNode) {
        setZIndex(parentNode.style.zIndex);
        parentNode.style.zIndex = HOVERzINDEX;
      }
    } else {
      props.setHotspotHovering(false);
      setTimeout(() => {
        if (elRef && elRef.current) {
          setZIndexRange(zIndexRange1);
          if (parentNode) {
            parentNode.style.zIndex = zIndex;
          }
        }
      }, 0);
    }
  };

  // Return null if hotspot is filtered out
  if (isFilteredOut && currentPano._id === '68d12cca208f82c724e17eb7') {
    return null;
  }

  if (scene && scene.indoorScene) {
    return (
      <Html
        ref={elRef}
        position={props.position}
        center={true}
        zIndexRange={zIndexRange}
        onVisible={onHotspotVisible}
        className="hotspot-khp-container"
      >
        <DPSceneHs
          // data={hotspot}
          onClickHotspot={props.onClick}
        />
      </Html>
    );
  }

  // if (media) {
  //   return (
  //     <Html
  //       ref={elRef}
  //       position={props.position}
  //       center={true}
  //       zIndexRange={zIndexRange}
  //       onVisible={onHotspotVisible}
  //       className="hotspot-khp-container"
  //     >
  //       <MediaHotspot
  //         media={media}
  //         height={1.5}
  //         goToScene={props.goToScene}
  //         handleApartmentPopup={props.handleApartmentPopup}
  //       />
  //     </Html>
  //   );
  // }



  if (media && !scene) {

    const rotatedDiagonalConfig = ROTATED_DIAGONAL_CONFIGS[media?._id] || ROTATED_DIAGONAL_CONFIGS[currentPano?._id];
    const renderOrderIndex = HOTSPOT_RENDER_ORDER.indexOf(media?._id);
    const hotspotZIndex = renderOrderIndex >= 0 ? renderOrderIndex + 1 : 0;

    if (rotatedDiagonalConfig?.angle) {
      return (
        <Html
          ref={elRef}
          position={props.position}
          center={true}
          zIndexRange={zIndexRange}
          onVisible={onHotspotVisible}
          className="hotspot-khp-container"
          style={{ zIndex: hotspotZIndex }}
        >
          <NTVRotatedDiagonalHotspot media={media} />
        </Html>
      );
    }


    return (
      <Html
        ref={elRef}
        position={props.position}
        center={true}
        zIndexRange={zIndexRange}
        onVisible={onHotspotVisible}
        className="hotspot-khp-container"
        style={{ zIndex: hotspotZIndex }}
      >
        <NTVMediaHotspotStyle media={media} />
      </Html>
    );
  }

  if ((scene?.outdoorScene) || scene?.amenitiesScene) {
    // Try scene._id first (for outdoor scenes), then fall back to media._id
    const hotspotIdForOrder = scene?._id || media?._id;
    const renderOrderIndex = HOTSPOT_RENDER_ORDER.indexOf(hotspotIdForOrder);
    const hotspotZIndex = renderOrderIndex >= 0 ? renderOrderIndex + 1 : 0;

    return (
      <Html
        ref={elRef}
        position={props.position}
        center={true}
        zIndexRange={zIndexRange}
        onVisible={onHotspotVisible}
        className="hotspot-khp-container"
        style={{ zIndex: hotspotZIndex }}
      >
        {ROTATED_DIAGONAL_CONFIGS[media?._id] ? (
          <NTVRotatedDiagonalHotspot media={media} />
        ) : (
          <NTVOutdoorHotspotStyle
            scene={props.scene}
            onClick={props.onClick}
            media={media}
            height={mediaHeight}
            goToScene={props.goToScene}
            handleApartmentPopup={props.handleApartmentPopup}
          />
        )}
      </Html>
    );
  }

  if (scene && scene.flycamOutdoorScene) {
    return (
      <Html
        ref={elRef}
        position={props.position}
        center={true}
        zIndexRange={zIndexRange}
        // style={styles}
        onVisible={onHotspotVisible}
        className="hotspot-khp-container"
      >
        <SceneFlycamOutdoorHotspot
          currentPano={currentPano}
          scene={props.scene}
          onClick={props.onClick}
          media={media}
          height={mediaHeight}
          goToScene={props.goToScene}
          handleApartmentPopup={props.handleApartmentPopup}
          dispatch={dispatch}
          onSelect={onSelect}
        />
      </Html>
    );
  }

  if (isClearSpaceStyle)
    return (
      <ThreeHtml ref={elRef} position={props.position}>
        <ClearSpaceHotspot
          hotspot={props.hotspot}
          groups={props.groups}
          onClick={props.onClick}
        />
      </ThreeHtml>
    );

  if (isAvoriazStyle) {
    return (
      <ThreeHtml ref={elRef} position={props.position}>
        <AvoriazHotspot
          hotspot={props.hotspot}
          groups={props.groups}
          onClick={props.onClick}
        />
      </ThreeHtml>
    );
  }

  if (
    hotspotStyle === HOTSPOT_UI.CALLOUT ||
    isTag ||
    isCalloutStyle ||
    isAquaStyle
  ) {
    return (
      <CalloutLine
        ref={elRef}
        hotspotRef={hotspotRef}
        canvasRef={canvasRef}
        position={props.position}
        center={true}
        zIndexRange={zIndexRange}
        style={styles}
        onVisible={onHotspotVisible}
        forceUp={props.forceUp}
        isAquaStyle={isAquaStyle}
        infoTagConfig={isTag && props.configsData}
      >
        <>
          {isAquaStyle ? (
            <>
              <Canvas ref={canvasRef} />
              {configsData &&
                configsData.baseIconType === 'circle_animation' ? (
                <CanvasPulsePoint />
              ) : (
                <CanvasCircle />
              )}
            </>
          ) : (
            <>
              <CanvasCircle />
              <Canvas ref={canvasRef} />
            </>
          )}

          <HotspotContainer ref={hotspotRef}>
            <HotspotIcon
              type={props.type}
              hotspot={props.hotspot}
              currentSceneCoords={props.currentSceneCoords}
              scene={props.scene}
              media={props.media}
              tour={props.tour}
              onClick={props.onClick}
              isHover={hovering}
              toggleHover={(val) => hoverToggled(val)}
              hoverIn={() => hoverToggled(true)}
              hoverOut={() => hoverToggled(false)}
              visited={props.visited}
              color={props.color || ''}
              scope={props.scope}
              hotspotStyle={hotspotStyle}
              isShowMapFloorPlanButton={isShowMapFloorPlanButton}
            />
          </HotspotContainer>
        </>
      </CalloutLine>
    );
  }

  return (
    <Html
      ref={elRef}
      position={props.position}
      center={true}
      zIndexRange={zIndexRange}
      style={styles}
      onVisible={onHotspotVisible}
    >
      <HotspotContainer>
        <HotspotIcon
          type={props.type}
          hotspot={props.hotspot}
          currentSceneCoords={props.currentSceneCoords}
          scene={props.scene}
          media={props.media}
          tour={props.tour}
          onClick={props.onClick}
          isHover={hovering}
          toggleHover={(val) => hoverToggled(val)}
          hoverIn={() => hoverToggled(true)}
          hoverOut={() => hoverToggled(false)}
          visited={props.visited}
          color={props.color || ''}
          scope={props.scope}
          params={params}
          hotspotStyle={hotspotStyle}
          isShowMapFloorPlanButton={isShowMapFloorPlanButton}
        />
      </HotspotContainer>
    </Html>
  );
};

export default React.memo(LineSingle);
